import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { SecurityDialog } from "@/components/SecurityDialog";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { LayoutDashboard, Inbox, MessageSquare, Kanban, Calendar, BarChart3, LogOut, Users, ChevronDown, Check, Building2, ShieldCheck, Rocket, Settings as SettingsIcon, ClipboardList, CreditCard, PlayCircle, MousePointerClick } from "lucide-react";
import { AccountStatusBanner } from "@/components/AccountStatusBanner";
import { toast } from "sonner";

const NAV = [
  { to: "/app", icon: LayoutDashboard, label: "Overview", end: true, id: "nav-overview" },
  { to: "/app/inbox", icon: Inbox, label: "Missed Leads", id: "nav-inbox" },
  { to: "/app/chat", icon: MessageSquare, label: "AI Chat", id: "nav-chat" },
  { to: "/app/pipeline", icon: Kanban, label: "Pipeline", id: "nav-pipeline" },
  { to: "/app/calendar", icon: Calendar, label: "Estimates", id: "nav-calendar" },
  { to: "/app/analytics", icon: BarChart3, label: "Analytics", id: "nav-analytics" },
  { to: "/app/team", icon: Users, label: "Team", id: "nav-team" },
  { to: "/app/settings", icon: SettingsIcon, label: "Settings", id: "nav-settings" },
  { to: "/app/go-live", icon: Rocket, label: "Go Live", id: "nav-go-live" },
  { to: "/app/onboarding", icon: ClipboardList, label: "Onboarding", id: "nav-onboarding" },
  { to: "/app/billing", icon: CreditCard, label: "Billing", id: "nav-billing" },
  { to: "/app/walkthrough", icon: MousePointerClick, label: "Interactive Demo", id: "nav-walkthrough", demoOnly: true },
  { to: "/app/in-action", icon: PlayCircle, label: "See RoofLead in Action", id: "nav-in-action", demoOnly: true },
];

const DEMO_HIDDEN = new Set(["nav-team", "nav-settings", "nav-go-live", "nav-onboarding", "nav-billing"]);

const roleStyle = {
  owner: "bg-orange-500/15 text-orange-300 border-orange-500/30",
  admin: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  member: "bg-slate-700 text-slate-300 border-slate-600",
};

export default function DashboardLayout() {
  const { user, org, role, memberships, logout, switchOrg } = useAuth();
  const [securityOpen, setSecurityOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleSwitch = async (org_id) => {
    if (org_id === org?.id) return;
    try {
      await switchOrg(org_id);
      toast.success("Switched organization");
      navigate("/app");
    } catch {
      toast.error("Could not switch org");
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] flex text-slate-100">
      <aside className="w-64 border-r border-slate-800 bg-slate-900/50 flex flex-col shrink-0">
        <div className="h-16 flex items-center px-5 border-b border-slate-800"><Logo /></div>

        {/* Org switcher */}
        <div className="p-3 border-b border-slate-800">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="org-switcher"
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg bg-slate-800/60 border border-slate-700 hover:border-orange-500/40 transition-colors text-left">
                <div className="w-8 h-8 rounded-md bg-orange-500/20 border border-orange-500/30 flex items-center justify-center shrink-0">
                  <Building2 className="w-4 h-4 text-orange-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-500 font-mono uppercase tracking-wider">Organization</p>
                  <p className="text-sm font-semibold truncate" data-testid="org-name">{org?.name || "—"}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-slate-900 border-slate-800 text-slate-100 w-60">
              <DropdownMenuLabel className="text-xs uppercase tracking-widest text-slate-500 font-mono">Your organizations</DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-slate-800" />
              {memberships.map((m) => (
                <DropdownMenuItem key={m.org_id} onSelect={() => handleSwitch(m.org_id)}
                  data-testid={`switch-${m.org_id}`}
                  className="focus:bg-slate-800 cursor-pointer">
                  <div className="flex items-center gap-2 w-full">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{m.org_name}</p>
                      <p className="text-xs text-slate-500 capitalize">{m.role}</p>
                    </div>
                    {m.org_id === org?.id && <Check className="w-4 h-4 text-orange-400 shrink-0" />}
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <div className="mt-2 flex justify-between items-center px-1">
            <Badge className={`text-xs border capitalize ${roleStyle[role] || roleStyle.member}`} data-testid="role-badge">{role}</Badge>
            <span className="text-xs text-slate-500 font-mono capitalize">{!org?.plan || org.plan === "trial" ? "onboarding" : org.plan}</span>
          </div>
        </div>

        <nav className="p-3 flex-1 space-y-1">
          {NAV.filter((n) => (org?.is_demo ? !DEMO_HIDDEN.has(n.id) : !n.demoOnly)).map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.id}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${isActive ? "bg-orange-500/15 text-orange-400 border border-orange-500/30" : "text-slate-300 hover:bg-slate-800 hover:text-white border border-transparent"}`}>
              <n.icon className="w-4 h-4" /> {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-slate-800">
          <div className="px-3 py-2 mb-2">
            <p className="text-sm font-semibold truncate">{user?.full_name}</p>
            <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          </div>
          <Button variant="ghost" onClick={() => setSecurityOpen(true)} data-testid="security-btn" className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800">
            <ShieldCheck className="w-4 h-4 mr-2" /> Security
          </Button>
          <Button variant="ghost" onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn" className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800">
            <LogOut className="w-4 h-4 mr-2" /> Log out
          </Button>
          <SecurityDialog open={securityOpen} onOpenChange={setSecurityOpen} />
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-x-hidden">
        <AccountStatusBanner />
        {org?.is_demo && DEMO_HIDDEN.has(`nav-${pathname.split("/")[2]}`) ? (
          <div className="p-10 text-slate-400" data-testid="demo-section-hidden">This section is hidden in the demo company.</div>
        ) : <Outlet />}
      </main>
    </div>
  );
}
